import React, { useState, useEffect, useRef } from 'react';
import clientDB from '../config/clientDatabase';
import './PowerBIViewer.css';

const PowerBIViewer = ({ user, company, reportConfig }) => {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    loadReports();
  }, [company]);

  const loadReports = () => {
    try {
      const companyId = company?.id;
      if (!companyId) {
        setReports([]);
        setLoading(false);
        return;
      }

      let allReports = [];

      // If admin, show reports from all client companies for testing
      if (company?.type === 'admin') {
        const allClients = clientDB.getClientCompanies();
        allClients.forEach(client => {
          const clientReports = clientDB.getCompanyPowerBIReports(client.id);
          const reportsWithClient = clientReports.map(report => ({
            ...report,
            clientName: client.name,
            clientId: client.id
          }));
          allReports = [...allReports, ...reportsWithClient];
        });
      } else {
        // For regular clients, only show their own reports
        allReports = clientDB.getCompanyPowerBIReports(companyId);
      }

      // Transform reports to match expected format
      const availableReports = allReports.map(report => ({
        ...report,
        thumbnail: '📊', // Default icon
        lastUpdated: new Date(report.addedDate || report.updatedDate || Date.now()),
        icon: '📊',
        // Add client name to title for admin view
        title: company?.type === 'admin' && report.clientName
          ? `${report.title} (${report.clientName})`
          : report.title
      }));

      setReports(availableReports);
      if (availableReports.length > 0) {
        setSelectedReport(availableReports[0]);
      }
    } catch (error) {
      console.error('Error loading Power BI reports:', error);
      setError('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 1) return 'Just updated';
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleReportSelect = (report) => {
    setSelectedReport(report);
    setError(null);
  };

  const handleIframeLoad = () => {
    // Handle successful iframe load
    setError(null);
  };

  const handleIframeError = () => {
    setError('Unable to load report. Please check your Power BI configuration.');
  };

  const refreshReport = () => {
    if (iframeRef.current) {
      iframeRef.current.src = selectedReport.embedUrl + '&refresh=true';
    }
  };

  const openInNewTab = () => {
    if (selectedReport) {
      window.open(selectedReport.embedUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="powerbi-viewer loading">
        <div className="loading-spinner"></div>
        <p>Loading reports...</p>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="powerbi-viewer empty">
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h2>No Reports Available</h2>
          <p>No Power BI reports are configured for your account.</p>
          <p>Contact your administrator to set up report access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="powerbi-viewer">
      <div className="viewer-content">
        {/* Report Sidebar */}
        <div className="reports-sidebar">
          <h3>Available Reports</h3>
          <div className="reports-list">
            {reports.map(report => (
              <div
                key={report.id}
                className={`report-item ${selectedReport?.id === report.id ? 'active' : ''}`}
                onClick={() => handleReportSelect(report)}
              >
                <div className="report-icon">{report.thumbnail}</div>
                <div className="report-details">
                  <h4>{report.title}</h4>
                  <p className="report-description">{report.description}</p>
                  <div className="report-meta">
                    <span className="report-category">{report.category}</span>
                    <span className="report-updated">{formatTimeAgo(report.lastUpdated)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="sidebar-actions">
            <button className="btn-secondary" onClick={refreshReport}>
              🔄 Refresh Report
            </button>
            <button className="btn-secondary" onClick={openInNewTab}>
              ↗️ Open in New Tab
            </button>
            {company?.type === 'admin' && (
              <button className="btn-secondary" onClick={() => alert('Use the Client Manager to manage Power BI reports for each client.')}>
                ⚙️ Manage Reports
              </button>
            )}
          </div>
        </div>

        {/* Report Viewer */}
        <div className="report-viewer">
          {selectedReport && (
            <div className="report-container">
                {error ? (
                  <div className="report-error">
                    <div className="error-icon">⚠️</div>
                    <h3>Report Load Error</h3>
                    <p>{error}</p>
                    <button className="btn-primary" onClick={() => setError(null)}>
                      Try Again
                    </button>
                  </div>
                ) : (
                  <iframe
                    ref={iframeRef}
                    src={selectedReport.embedUrl}
                    title={selectedReport.title}
                    className="powerbi-iframe"
                    onLoad={handleIframeLoad}
                    onError={handleIframeError}
                    allowFullScreen
                    frameBorder="0"
                    scrolling="no"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: '8px',
                      display: 'block'
                    }}
                  />
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PowerBIViewer;